import { FC, useContext, useMemo, useState } from "react"

import { CheckOutlined, CloseOutlined, LeftOutlined } from "@ant-design/icons"
import { Card, Descriptions, Flex, Skeleton, Space, Tag, Grid } from "antd"
import { Link, useNavigate, useParams } from "react-router-dom"

import classes from "@/_styles/cardById.module.scss"
import variables from "@/_styles/variables.module.scss"
import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { Resource, Scope } from "@/constants/permission"
import { FINANCE_RETURN_FUNDS_QUERY_KEY } from "@/constants/query"
import { CustomerContext } from "@/context/CustomerContext"

import { useFinanceReturnFundsMutation } from "@/hook/Finance/ReturnFunds/useFinanceRetrunFundsMutation"
import { useFinanceReturnFundsByIdQuery } from "@/hook/Finance/ReturnFunds/useFinanceReturnFundsQuery"
import { useMessage } from "@/hook/useMessage"
import { usePermission } from "@/hook/usePermission"

import { IFinanceReturnFunds, ReturnStatus } from "@/types/IFinanceReturnFunds"

import { paths } from "@/router/paths"

import { dateTimeFormat, formatDate } from "@/utils/formatDate"
import { getMoney } from "@/utils/formatNumber"


const FinanceReturnFundsById: FC = () => {

    const customerContext = useContext(CustomerContext)
    const { hasPermission } = usePermission()
    const navigate = useNavigate()
    const { id } = useParams<{ id?: string }>()

    const { data, isLoading, isError } = useFinanceReturnFundsByIdQuery(Number(id))

    const [returnFund, setReturnFund] = useState<IFinanceReturnFunds>()

    const { changeStatusByAdminMutation, changeStatusMutation } = useFinanceReturnFundsMutation()

    const { useBreakpoint } = Grid
    const screens = useBreakpoint()
    const direction = screens.xs ? "vertical" : "horizontal"

    const onBackHandler = () => {
        navigate(paths.financeReturnFunds)
    }

    const mainTitle = useMemo(() => {
        if (data) {
            setReturnFund(data.response)
            return <div className={classes["header"]}>
                <Flex align="center" gap="20px">
                    <ColorifiedButton
                        themecolor="blue"
                        type="primary"
                        shape="circle"
                        size="small"
                        icon={<LeftOutlined />}
                        onClick={onBackHandler}
                    />
                    <span>Запрос на возврат средств от {formatDate(data.response.date)}</span>
                </Flex>
                <div>
                    {!customerContext.isCustomer() &&
                        data.response.customer && (
                            <Tag key={"client-tag" + data.response.customer.id}>
                                <Link to={paths.customerClientById(data.response.customer.id)}>
                                    {data.response.customer.name}
                                </Link>
                            </Tag>
                        )
                    }
                </div>
            </div>
        }
    }, [data])

    const onCancelReturnByCustomerHandler = async () => {
        if (!returnFund)
            return

        await changeStatusMutation.mutateAsync({
            returnId: returnFund?.id,
            status: ReturnStatus.Cancelled
        })
    }


    const onCancelReturnHandler = async () => {
        if (!returnFund)
            return

        await changeStatusByAdminMutation.mutateAsync({
            returnId: returnFund?.id,
            status: ReturnStatus.Cancelled
        })
    }

    const onApproveHandler = async () => {
        if (!returnFund)
            return

        await changeStatusByAdminMutation.mutateAsync({
            returnId: returnFund?.id,
            status: ReturnStatus.Done
        })
    }

    return (
        <>
            <Skeleton loading={isLoading}>
                {data && returnFund && (
                    <>
                        <Card bordered className={classes["card"]}>
                            <Space direction="vertical" className={classes["wrapper"]}>
                                <Descriptions title={mainTitle}
                                    layout="vertical"
                                    column={3}
                                    className={classes["descriptions"]}
                                    labelStyle={{
                                        fontWeight: 600,
                                        color: variables.tableSubTitleColor,
                                        marginBottom: "-30px"
                                    }}
                                    contentStyle={{
                                        paddingBottom: "15px"
                                    }}
                                >
                                    <Descriptions.Item label="Дата">
                                        {formatDate(returnFund.date)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Сумма возврата">
                                        {getMoney(returnFund.sum)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Создано">
                                        {formatDate(returnFund.createdOn, dateTimeFormat)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Статус">
                                        {returnFund.statusText}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Space>
                        </Card>

                        <Card title="Реквизиты" className={classes["card"]}>
                            <Descriptions
                                layout="vertical"
                                column={3}
                                className={classes["descriptions"]}
                                labelStyle={{
                                    fontWeight: 600,
                                    color: variables.tableSubTitleColor,
                                    marginBottom: "-30px"
                                }}
                                contentStyle={{
                                    paddingBottom: "15px"
                                }}
                            >
                                <Descriptions.Item label="ИНН">
                                    {returnFund.requisites.inn}
                                </Descriptions.Item>
                                <Descriptions.Item label="ОГРН">
                                    {returnFund.requisites.ogrn}
                                </Descriptions.Item>
                                <Descriptions.Item label="КПП">
                                    {returnFund.requisites.kpp}
                                </Descriptions.Item>
                                <Descriptions.Item label="Получатель платежа" span={3}>
                                    {returnFund.requisites.recieverName}
                                </Descriptions.Item>
                                <Descriptions.Item label="Наименование банка" span={3}>
                                    {returnFund.requisites.bankName}
                                </Descriptions.Item>
                                <Descriptions.Item label="Расчетный счет" span={2}>
                                    {returnFund.requisites.currentAccount}
                                </Descriptions.Item>
                                <Descriptions.Item label="БИК">
                                    {returnFund.requisites.bik}
                                </Descriptions.Item>
                                <Descriptions.Item label="КБК">
                                    {returnFund.requisites.kbk}
                                </Descriptions.Item>
                                <Descriptions.Item label="ОКТМО">
                                    {returnFund.requisites.oktmo}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card title="Статус" className={classes["card"]}>
                            <Space direction={direction}>
                                {returnFund.statusText}
                                {returnFund.status === ReturnStatus.InProgress && hasPermission(Resource.FinanceReturnFunds, Scope.Update) && (
                                    <Space>
                                        {customerContext.isCustomer() && //Customer
                                            <>
                                                <ColorifiedButton
                                                    themecolor="orange"
                                                    icon={<CloseOutlined />}
                                                    type={"primary"}
                                                    onClick={onCancelReturnByCustomerHandler}
                                                >
                                                    Отменить
                                                </ColorifiedButton>
                                            </>
                                        }
                                        {!customerContext.isCustomer() && //Manager & Admin
                                            <>
                                                <ColorifiedButton
                                                    themecolor="blue"
                                                    icon={<CheckOutlined />}
                                                    type={"primary"}
                                                    onClick={onApproveHandler}
                                                >
                                                    Подтвердить
                                                </ColorifiedButton>
                                                <ColorifiedButton
                                                    themecolor="orange"
                                                    icon={<CloseOutlined />}
                                                    type={"primary"}
                                                    onClick={onCancelReturnHandler}
                                                >
                                                    Отменить
                                                </ColorifiedButton>
                                            </>
                                        }
                                    </Space>
                                )}
                            </Space>
                        </Card>
                    </>
                )}
            </Skeleton>
        </>
    )
}
export default FinanceReturnFundsById